import * as Sentry from '@sentry/react';

export const DEFAULT_MEMORY_DELTA_THRESHOLD_MB = 50;
export const DEFAULT_MEMORY_USAGE_HIGH_THRESHOLD = 0.8;
export const DEFAULT_MEMORY_USAGE_CRITICAL_THRESHOLD = 0.95;

export const mbToBytes = (mb) => mb * 1024 * 1024;
export const bytesToMb = (bytes) => bytes / 1024 / 1024;

const isTestEnvironment = () => {
  return process.env.NODE_ENV === 'test' || typeof jest !== 'undefined';
};

export default class MemoryMonitor {
  constructor(stores) {
    this.stores = stores;
    this.memoryHistory = [];
    this.continuousMonitoringInterval = null;
    this.continuousStackMonitoringInterval = null;
    this.queueProcessingMonitor = null;
    this.baselineMemory = null;
  }

  areFeatureFlagsEnabled() {
    return (
      this.stores.featureStore.featureFlags?.SENTRY &&
      this.stores.messengerStore.isSentryMemoryProfilingEnabled
    );
  }

  isEnabled() {
    if (isTestEnvironment()) return false;

    return this.areFeatureFlagsEnabled() && 'memory' in performance;
  }

  trackMemoryMetrics = (context = 'general') => {
    if (this.isEnabled()) {
      try {
        const memoryInfo = performance.memory;
        const usedBytes = memoryInfo.usedJSHeapSize;
        const totalBytes = memoryInfo.totalJSHeapSize;
        const limitBytes = memoryInfo.jsHeapSizeLimit;

        const memoryUsagePercentage = usedBytes / limitBytes;

        if (context === 'baseline') {
          Sentry.logger.trace(
            Sentry.logger.fmt`Starting memory monitoring, memory usage: ${
              memoryUsagePercentage * 100
            }%`
          );
        }

        const highThreshold = this.stores.messengerStore.memoryUsageHighThreshold;
        const criticalThreshold = this.stores.messengerStore.memoryUsageCriticalThreshold;

        if (memoryUsagePercentage > highThreshold) {
          const data = {
            context,
            usedMB: Math.round(usedBytes / 1024 / 1024),
            totalMB: Math.round(totalBytes / 1024 / 1024),
            limitMB: Math.round(limitBytes / 1024 / 1024),
            usagePercentage: Math.round(memoryUsagePercentage * 100),
            highThreshold: Math.round(highThreshold * 100),
            criticalThreshold: Math.round(criticalThreshold * 100),
          };

          Sentry.logger.warn('High memory usage detected', data);

          if (memoryUsagePercentage > criticalThreshold) {
            Sentry.logger.error('Critical memory usage detected', data);
          }
        }

        return {
          usedBytes,
          totalBytes,
          limitBytes,
          usedMB: Math.round(usedBytes / 1024 / 1024),
          totalMB: Math.round(totalBytes / 1024 / 1024),
          limitMB: Math.round(limitBytes / 1024 / 1024),
          usagePercentage: Math.round(memoryUsagePercentage * 100),
        };
      } catch (error) {
        Sentry.logger.error('Error tracking memory metrics', { context, error: error.message });
        return null;
      }
    }
    return null;
  };

  // Start continuous memory monitoring
  startMemoryMonitoring = (intervalMs = 30000) => {
    if (this.isEnabled() && !isTestEnvironment()) {
      // Set baseline memory
      if (!this.baselineMemory) {
        this.baselineMemory = this.trackMemoryMetrics('baseline');
      }

      // Set up EntityStore queue processing monitoring
      this.trackEntityStoreQueueProcessing();

      // Clear existing interval if any
      if (this.continuousMonitoringInterval) {
        clearInterval(this.continuousMonitoringInterval);
      }

      this.continuousMonitoringInterval = setInterval(() => {
        const currentMemory = this.trackMemoryMetrics('monitoring');

        if (currentMemory) {
          const now = Date.now();

          // Keep only last 20 measurements
          this.memoryHistory.push({
            timestamp: now,
            ...currentMemory,
          });

          if (this.memoryHistory.length > 20) {
            this.memoryHistory.shift();
          }

          // Check for memory leaks (consistent growth over time)
          if (this.memoryHistory.length >= 10) {
            const recent = this.memoryHistory.slice(-5);
            const older = this.memoryHistory.slice(-10, -5);

            const recentAvgBytes =
              recent.reduce((sum, entry) => sum + entry.usedBytes, 0) / recent.length;
            const olderAvgBytes =
              older.reduce((sum, entry) => sum + entry.usedBytes, 0) / older.length;

            const growthRateBytes = recentAvgBytes - olderAvgBytes;
            const growthRateMB = growthRateBytes / 1024 / 1024;

            if (growthRateMB > this.stores.messengerStore.memoryDeltaThresholdMB) {
              const data = {
                growthRateMB: Math.round(growthRateMB),
                recentAverageMB: Math.round(recentAvgBytes / 1024 / 1024),
                olderAverageMB: Math.round(olderAvgBytes / 1024 / 1024),
                measurementPeriodMs: intervalMs,
              };
              Sentry.logger.warn('Potential memory leak detected', data);
            }
          }
        }
      }, intervalMs);
    }
    if (this.areFeatureFlagsEnabled()) {
      // Clear existing interval if any
      if (this.continuousStackMonitoringInterval) {
        clearInterval(this.continuousStackMonitoringInterval);
      }

      this.continuousStackMonitoringInterval = setInterval(() => {
        this.monitorEntityStoreQueue(); // Monitor queue status
      }, intervalMs);
    }
  };

  // Stop continuous memory monitoring
  stopMemoryMonitoring = () => {
    if (this.continuousMonitoringInterval) {
      clearInterval(this.continuousMonitoringInterval);
      this.continuousMonitoringInterval = null;
    }
    if (this.continuousStackMonitoringInterval) {
      clearInterval(this.continuousStackMonitoringInterval);
      this.continuousStackMonitoringInterval = null;
    }
    if (this.queueProcessingMonitor) {
      clearInterval(this.queueProcessingMonitor);
      this.queueProcessingMonitor = null;
    }
  };

  // Monitor EntityStore queue specifically
  monitorEntityStoreQueue = () => {
    if (!this.areFeatureFlagsEnabled()) return null;

    try {
      // Access the queue from global mobxStores
      const entityStore = window.mobxStores?.entityStore;
      if (!entityStore || !entityStore.queue) {
        Sentry.logger.warn('EntityStore queue not accessible');
        return null;
      }

      const queueSize = entityStore.queue.size;
      const replayStartQueueSize = entityStore.replayStartQueue?.length || 0;
      const processedEventsCount = entityStore._processedEventsCount || 0;
      const relationsUpdatedCount = entityStore._relationsUpdatedCount || 0;

      const data = {
        queueSize,
        replayStartQueueSize,
        processedEventsCount,
        relationsUpdatedCount,
        totalPendingEvents: queueSize + replayStartQueueSize,
      };

      // Alert if queue is growing too large
      if (queueSize > 1000) {
        Sentry.logger.warn('EntityStore queue overflow detected', data);
      }

      // Critical alert if queue is massive
      if (queueSize > 10000) {
        Sentry.logger.error('EntityStore queue overflow critical', data);
      }

      return {
        queueSize,
        replayStartQueueSize,
        processedEventsCount,
        relationsUpdatedCount,
        totalPendingEvents: queueSize + replayStartQueueSize,
      };
    } catch (error) {
      Sentry.logger.error('Error monitoring EntityStore queue', { error: error.message });
      return null;
    }
  };

  // Track memory usage during queue processing using event monitoring
  trackEntityStoreQueueProcessing = () => {
    if (!this.isEnabled() || isTestEnvironment()) return;

    try {
      const entityStore = window.mobxStores?.entityStore;
      if (!entityStore) return;

      // Monitor queue processing by tracking queue size changes
      let lastQueueSize = entityStore.queue.size;
      let processingStartTime = null;
      let processingStartQueueSize = null;
      let stableTickCount = 0;

      if (this.queueProcessingMonitor) {
        clearInterval(this.queueProcessingMonitor);
        this.queueProcessingMonitor = null;
      }

      // Set up interval to detect queue processing
      this.queueProcessingMonitor = setInterval(() => {
        const currentQueueSize = entityStore.queue.size;

        // Detect when processing starts (queue size decreases)
        if (currentQueueSize < lastQueueSize && processingStartTime === null) {
          processingStartTime = Date.now();
          processingStartQueueSize = lastQueueSize;
          stableTickCount = 0;
          this.trackMemoryMetrics('entityStore.processQueues.start');
        }

        // Track stable ticks (queue size hasn't changed)
        if (currentQueueSize === lastQueueSize) {
          stableTickCount++;
        } else {
          stableTickCount = 0;
        }

        // Detect when processing ends (queue size stable for multiple ticks)
        if (processingStartTime !== null && stableTickCount >= 3) {
          const processingDuration = Date.now() - processingStartTime;
          const processed = processingStartQueueSize - currentQueueSize;

          Sentry.logger.info('Queue processing detected', {
            processed,
            durationMs: processingDuration,
            remainingQueueSize: currentQueueSize,
          });

          this.trackMemoryMetrics('entityStore.processQueues.end');
          processingStartTime = null;
          processingStartQueueSize = null;
          stableTickCount = 0;
        }

        lastQueueSize = currentQueueSize;
      }, 100); // Check every 100ms for processing activity

      Sentry.logger.trace('EntityStore queue processing monitoring enabled (event-based)');
    } catch (error) {
      Sentry.logger.error('Error setting up EntityStore queue monitoring', {
        error: error.message,
      });
    }
  };

  // Cleanup method
  destroy = () => {
    this.stopMemoryMonitoring();
    if (this.queueProcessingMonitor) {
      clearInterval(this.queueProcessingMonitor);
      this.queueProcessingMonitor = null;
    }
    this.memoryHistory = [];
    this.baselineMemory = null;
  };
}
